import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoInformationCircleOutline } from 'react-icons/io5';
import WebButton from '../../../../component/library/webButton';
import SubscribeButton from './SubscribeButton';
import { OrderSummarySkeleton } from './ProductDetailSkeletons';
import { getTotalSelectedQuantity, getPriceForQuantity } from './utils';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import {
  FaShieldAlt,
  FaTruck,
  FaUndo,
  FaMoneyBillWave,
  FaLock,
} from 'react-icons/fa';
import visaLogo from '../../../assets/images/home/payment_icons/visa.png';
import mastercardLogo from '../../../assets/images/home/payment_icons/mastercard.png';
import upiLogo from '../../../assets/images/home/payment_icons/upi.png';
import rupayLogo from '../../../assets/images/home/payment_icons/rupay.png';
import ComImportLogo from '../../../assets/images/common/comimport.png';

const OrderSummary = ({
  selectedVariants,
  pricingMode,
  onAddToCart,
  onBuyNow,
  product,
  searchKeyword,
  isLoggedIn,
  authToken,
  isLoading,
  isWholesale,
  priceRanges,
  variantPrices = {},
  minVariantPrice,
  variantDetails,
  totalWholesalePrice,
  isMinMaxRange,
  pricingData,
  loadingPricing,
  hasFetchedPricing,
  autoUpdating,
  onFetchWholesalePricing,
  askForPricingQuotation,
  setIsOpenAskInquiry,
  shouldShowWeightMessage = false,
  hasZeroDimensions = false,
  hasPriceLogs = false,
  dbVariantPrices = null,
}) => {
  const message = useMessage();
  const navigate = useNavigate();

  // UI & data states
  const [showChargeDetails, setShowChargeDetails] = useState(false);
  const [isAddedToCart, setIsAddedToCart] = useState(false);

  // Mobile states
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Show "Final Price" label after discount/pricing has been fetched
  const [showTotalLabel, setShowTotalLabel] = useState(false);

  // safe mobile detection (avoids SSR issues)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // close mobile sheet when switching to desktop
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
    // Also close tooltip when switching to desktop
    if (!isMobile) setShowTooltip(false);
  }, [isMobile]);

  // Effect to freeze/unfreeze body scrolling when sheet is open
  useEffect(() => {
    if (showTooltip) {
      // Save current scroll position and disable scrolling
      const scrollY = window.scrollY;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    } else {
      // Restore scrolling
      const body = document.body;
      const scrollY = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';

      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function
    return () => {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
    };
  }, [showTooltip]);

  // calculate totals (original logic preserved)
  const calculateTotals = () => {
    const totalQuantity = getTotalSelectedQuantity(selectedVariants);
    const variantCount = Object.values(selectedVariants).filter(v => v.quantity > 0).length;

    if (!variantCount) return { total: 0, totalQuantity: 0, averagePrice: 0, variantCount: 0, dbPriceTotal: 0 };

    const sampleVariant = Object.values(selectedVariants).find(v => v.quantity > 0);
    const basePrice = parseFloat(sampleVariant?.ap || sampleVariant?.cp || sampleVariant?.consignPrice || sampleVariant?.fenxiaoPriceInfo?.offerPrice || 0);

    let rangePrice = null;

    // Calculate range price from priceRanges correctly
    if (isWholesale && priceRanges && priceRanges.length > 0) {
      const activeRange = getPriceForQuantity(basePrice, totalQuantity, priceRanges);
      if (activeRange) {
        rangePrice = parseFloat(activeRange.finalPrice || activeRange.price || 0);
      } else if (priceRanges[0]) {
        rangePrice = parseFloat(priceRanges[0].finalPrice || priceRanges[0].price || 0);
      }
    }

    let minMaxPrice = null;
    let dbPriceTotal = 0; // Total using DB variant prices

    const total = Object.values(selectedVariants).reduce((sum, v) => {
      if (v.quantity > 0) {
        let price;

        // If hasPriceLogs is true, use DB variant prices for calculation
        // Use wholesaleFinalPrice for wholesale mode, finalPrice for dropshipping mode
        if (hasPriceLogs && dbVariantPrices && dbVariantPrices[v.skuId]) {
          const dbVariant = dbVariantPrices[v.skuId];
          const dbPrice = isWholesale
            ? parseFloat(dbVariant.wholesaleFinalPrice || dbVariant.finalPrice || 0)
            : parseFloat(dbVariant.finalPrice || 0);
          dbPriceTotal += dbPrice * v.quantity;
        }

        if (isWholesale) {
          // Use wholesaleFinalPrice for wholesale mode (calculated from consignPrice)
          price = parseFloat(v.wholesaleFinalPrice || v.finalPrice || 0) || 0;
          minMaxPrice = v.wholesaleFinalPrice || v.finalPrice;
        } else {
          // Use dropshipping price - prioritize variant-specific price fields
          // Each variant has its own onePiecePrice which is variant-specific
          // Priority: variantPrices[skuId].finalPrice > variant's onePiecePrice > other fields
          const dropshippingPrice = variantPrices[v.skuId]?.finalPrice;

          // If variantPrices has the calculated price, use it (this is what's displayed in the UI)
          if (dropshippingPrice && dropshippingPrice > 0) {
            price = parseFloat(dropshippingPrice);
          } else {
            // Use variant's own specific price fields
            // onePiecePrice is variant-specific, so use it before finalPrice (which might be minimum)
            const variantSpecificPrice = parseFloat(v.fenxiaoPriceInfo?.onePiecePrice || 0);

            if (variantSpecificPrice > 0) {
              price = variantSpecificPrice;
            } else {
              // Fallback to other price fields
              price = parseFloat(
                v.finalPrice ||
                v.consignPrice ||
                v.fenxiaoPriceInfo?.offerPrice ||
                0
              ) || 0;
            }
          }
        }
        return sum + price * v.quantity;
      }
      return sum;
    }, 0);

    const averagePrice = totalQuantity > 0 ? total / totalQuantity : 0;
    const dbAveragePrice = totalQuantity > 0 ? dbPriceTotal / totalQuantity : 0;

    return {
      total,
      totalQuantity,
      averagePrice,
      variantCount,
      basePrice,
      rangePrice,
      minMaxPrice,
      dbPriceTotal,
      dbAveragePrice,
    };
  };

  const { total, totalQuantity, averagePrice, variantCount, basePrice, rangePrice, minMaxPrice, dbPriceTotal, dbAveragePrice } = calculateTotals();
  const hasSelection = variantCount > 0;

  // Reset isAddedToCart when selection changes
  useEffect(() => {
    setIsAddedToCart(false);
  }, [selectedVariants, totalQuantity]);

  // dropshipping min price (for SubscribeButton)
  const sp = minVariantPrice;

  // when pricingData becomes available, set showTotalLabel to true so top bar switches to "Final Price"
  // and auto-open modal on mobile
  useEffect(() => {
    if (pricingData) {
      setShowTotalLabel(true);
      // Auto-open modal on mobile when pricing data is received (only if not already open)
      if (isMobile && hasSelection && !showTooltip && !loadingPricing) {
        // Small delay to ensure smooth transition
        setTimeout(() => {
          setShowTooltip(true);
        }, 300);
      }
    }
    // if no selection, reset label and close modal
    if (!hasSelection) {
      setShowTotalLabel(false);
      setShowTooltip(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingData, isMobile, hasSelection, loadingPricing]);

  // Helper function to handle wholesale pricing fetch (now works without login)
  const handleWholesalePricingFetch = async (auto = false) => {
    if (onFetchWholesalePricing) {
      await onFetchWholesalePricing(auto);
    }
  };

  // Wrapper function to handle add to cart with login check
  const handleAddToCart = async () => {
    if (!isLoggedIn) {
      message.showToast('Login to add to cart', ToastTypes.WARNING);
      return;
    }
    if (onAddToCart) {
      try {
        await onAddToCart(discountAmount);
        setIsAddedToCart(true);
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }
  };

  // When variants/qty change:
  // - DESKTOP wholesale: after first manual pricing, auto-update on every change
  // - MOBILE wholesale: after first manual pricing, auto-update on every change (same as desktop)
  // NOTE: The parent component (productDetails/index.js) already handles auto-fetching pricing
  // when selectedVariants changes. This effect only updates the label state.
  useEffect(() => {
    // If nothing selected, always reset label
    if (!hasSelection) {
      setShowTotalLabel(false);
      return;
    }

    if (isWholesale) {
      // Both DESKTOP and MOBILE: after the first time user clicked "Get Discounted Prices",
      // show the final price label
      if (hasFetchedPricing && totalQuantity > 0) {
        // Just update the label - don't fetch pricing here as parent already does it
        setShowTotalLabel(true);
      } else {
        // If pricing not fetched yet, reset label to show "Get Discounted Price" button
        setShowTotalLabel(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariants, totalQuantity, isWholesale, isMobile, isLoggedIn, hasFetchedPricing]);

  // Derived pricing values from parent
  const chnProcurement = pricingData?.LS || 0;
  const customDuty = pricingData?.CD || 0;
  const indProcurement = (pricingData?.IF || 0) + (pricingData?.LMD || 0);
  const platformFees = pricingData?.PF || 0;
  const gstProduct = pricingData?.GST_PR || 0;
  const gstFreight = pricingData?.GST_FR || 0;
  const gstPlatform = pricingData?.GST_PF || 0;
  const totalGst = gstProduct + gstFreight + gstPlatform;
  const wholesaleTotal = pricingData?.FBP || 0;
  const finalFBP = pricingData?.finalFBP || 0; // Get finalFBP from response
  const totalFBPAllVariants = pricingData?.totalFBPAllVariants || 0; // Get totalFBPAllVariants from response
  const finalPrice = pricingData?.finalPrice || 0; // Get finalPrice from response
  const wholesaleAveragePrice = totalQuantity > 0 ? wholesaleTotal / totalQuantity : 0;

  const rangedItemCost = isWholesale && rangePrice ? rangePrice : 0;

  // --- FIX: Always sum each variant's calculatedFBP (or fallback price) times quantity ---
  let computedGrossTotal = 0;
  if (hasPriceLogs && dbPriceTotal > 0) {
    computedGrossTotal = dbPriceTotal;
  } else if (Array.isArray(priceRanges) && priceRanges.length > 2) {
    // Use the correct price bracket for the total selected quantity
    const totalQty = getTotalSelectedQuantity(selectedVariants) || 0;
    const sortedRanges = [...priceRanges].sort((a, b) => a.startQuantity - b.startQuantity);
    let activeRange = sortedRanges[0];
    for (let i = 0; i < sortedRanges.length; i++) {
      if (totalQty >= sortedRanges[i].startQuantity) {
        activeRange = sortedRanges[i];
      } else {
        break;
      }
    }
    const price = activeRange && activeRange.calculatedFBP !== undefined ? Number(activeRange.calculatedFBP) : 0;
    computedGrossTotal = price * totalQty;
  } else {
    computedGrossTotal = Object.values(selectedVariants).reduce((sum, v) => {
      if (v.quantity > 0) {
        let price = v.calculatedFBP || v.finalPrice || v.ap || v.cp || v.consignPrice || (v.fenxiaoPriceInfo && v.fenxiaoPriceInfo.offerPrice) || 0;
        return sum + price * v.quantity;
      }
      return sum;
    }, 0);
  }

  const grossTotalAmount = computedGrossTotal || 0;

  // Calculate discount: grossTotalAmount - finalFBP
  // Use finalFBP from API response for discount calculation
  const hasValidPricingData = pricingData && typeof pricingData.finalFBP === 'number' && pricingData.finalFBP > 0;
  const rawDiscountAmount = hasValidPricingData ? grossTotalAmount - finalFBP : 0;
  const discountAmount = rawDiscountAmount >= 0 ? rawDiscountAmount : 0;
  // If discount is negative, absorb the extra into procurement charges
  const adjustedProcurementCharges = (pricingData?.procurementCharges || 0) + (rawDiscountAmount < 0 ? Math.abs(rawDiscountAmount) : 0);

  // Debug logging
  console.log('Discount Debug:', {
    grossTotalAmount,
    finalFBP,
    totalFBPAllVariants,
    finalPrice,
    discountAmount,
    pricingData,
    hasPricingData: !!pricingData,
    hasValidPricingData,
    finalFBPType: typeof pricingData?.finalFBP,
    showWarning: pricingData?.showWarning
  });

  const hideAddToCart = isWholesale && (!hasFetchedPricing || loadingPricing || autoUpdating);

  // Mobile CTA: for wholesale, fetch pricing and auto-open modal; sheet opens only from arrow
  const handleMobileCTA = async e => {
    e.stopPropagation();
    if (isWholesale) {
      if (!loadingPricing && hasSelection) {
        await handleWholesalePricingFetch(false);
        // Modal will auto-open via the pricingData useEffect when response is received
      }
    }
  };

  // Mobile arrow click toggles dropdown sheet
  const toggleMobileDropdown = () => {
    setMobileOpen(p => !p);
  };

  if (isLoading) return <OrderSummarySkeleton />;

  // compute top-bar displayed amount: if showTotalLabel and finalFBP > 0 use finalFBP, else if showTotalLabel and wholesaleTotal > 0 use wholesaleTotal else show grossTotalAmount
  const topBarAmount = showTotalLabel && finalPrice > 0 ? finalPrice : showTotalLabel && wholesaleTotal > 0 ? wholesaleTotal : grossTotalAmount;
  const topBarLabel = showTotalLabel ? 'Final Price' : 'Gross Total Amount';

  const ComImportLogoImage = ({ className = "", alt = "comimport" }) => (
    <img
      src={ComImportLogo}
      alt={alt}
      className={className}
    />
  );

  const PaymentLogo = ({ src, alt, className = "" }) => (
    <img
      src={src}
      alt={alt}
      className={`w-6 h-4 object-contain ${className}`}
    />
  );

  // Shared Price Details Content Component
  const PriceDetailsContent = () => (
    <>
      <div className="mb-2">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[13px] text-gray-600">Mode :</span>
          <span className="text-[13px] font-medium capitalize">{pricingMode}</span>
        </div>

        {hasSelection ? (
          <>
            <div className="p-4 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
              {/* Header */}
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-[15px] font-semibold text-gray-800 flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h18M9 3v18M15 3v18M3 9h18M3 15h18" />
                  </svg>
                  Order Summary
                </h3>
              </div>

              {/* Quantity */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500 font-medium tracking-wide">Quantity</span>
                <span className="text-[15px] font-semibold text-gray-900">{totalQuantity} units</span>
              </div>

              {askForPricingQuotation ? (
                <p className="text-[13px] text-red-600 font-medium mt-2 mb-0">To get price info send inquiry</p>
              ) : (
                isWholesale && (
                  <>
                    <hr className="my-3 border-gray-200" />
                    {/* Gross Total Amount */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500 font-medium tracking-wide">Gross Total Amount</span>
                      <span className="text-[15px] font-semibold text-black-600">₹{grossTotalAmount.toLocaleString()}</span>
                    </div>

                    {/* Updating spinner */}
                    {autoUpdating && (
                      <div className="mt-3 p-3 border border-gray-200 rounded bg-grey-50 text-center text-[13px]">
                        <div className="flex items-center justify-center gap-2">
                          <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                          </svg>
                          Updating...
                        </div>
                      </div>
                    )}

                    {/* Pricing breakdown — merged into same card */}
                    {pricingData && !loadingPricing && !autoUpdating && (
                      <>
                        <hr className="my-3 border-gray-200" />
                        <div className="space-y-3">
                          {/* Procurement Charges */}
                          {adjustedProcurementCharges > 0 && (
                            <>
                              <div className="flex justify-between items-center relative">
                                <div className="flex items-center">
                                  <span className="text-[13px] font-medium text-gray-600">Procurement Charges</span>
                                  <button
                                    onMouseEnter={() => setShowChargeDetails(true)}
                                    onMouseLeave={() => setShowChargeDetails(false)}
                                    className="ml-1 p-1 text-gray-500 hover:text-blue-600"
                                    type="button"
                                    tabIndex={0}
                                  >
                                    <IoInformationCircleOutline size={15} />
                                  </button>
                                  {showChargeDetails && (
                                    <div className="absolute left-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
                                      <h5 className="font-semibold text-gray-800 mb-2 text-[12px]">
                                        Procurement Charges GST Breakdown
                                      </h5>
                                    </div>
                                  )}
                                </div>
                                <div className="text-right">
                                  <span className="text-[16px] font-semibold text-gray-800">₹{adjustedProcurementCharges.toFixed(2)}</span>
                                </div>
                              </div>
                              {pricingData?.showWarning && (
                                <div className="text-[11px] text-orange-600 mt-1 ml-0">
                                  * Calculated based on Assumed Weight of the product. Send Enquiry for accurate pricing.
                                </div>
                              )}
                            </>
                          )}

                          {/* Total Amount (before discount) */}
                          {adjustedProcurementCharges > 0 && discountAmount > 0 && (
                            <div className="flex justify-between items-center">
                              <span className="text-[13px] font-medium text-gray-600">Total Amount</span>
                              <div className="text-right">
                                <span className="text-[16px] font-semibold text-gray-800">₹{(grossTotalAmount + adjustedProcurementCharges).toFixed(2)}</span>
                              </div>
                            </div>
                          )}

                          {/* Discount */}
                          {discountAmount > 0 && (
                            <div className="flex justify-between items-center pb-2">
                              <span className="text-[13px] font-medium text-gray-600">Discount</span>
                              <div className="text-right">
                                <span className="text-[16px] font-bold text-green-600">₹{discountAmount.toFixed(2)}</span>
                                <span className="text-[11px] text-gray-500 ml-2">
                                  {grossTotalAmount > 0 ? ((discountAmount / grossTotalAmount) * 100).toFixed(1) : '0.0'}% off
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Final Price */}
                          {finalPrice > 0 && (
                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                              <span className="text-[14px] font-semibold text-gray-800">Final Price</span>
                              <div className="text-right">
                                <span className="text-[20px] font-bold text-green-600">₹{finalPrice.toFixed(2)}</span>
                                <span className="text-[11px] text-gray-500 ml-2">
                                  ₹{totalQuantity > 0 ? (finalPrice / totalQuantity).toFixed(2) : '0.00'}/piece
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Add to Cart Button */}
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          {pricingData?.showWarning ? (
                            <div className="flex gap-3">
                              <WebButton
                                className="flex-1 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-orange-700 hover:to-orange-800 transition-all duration-200"
                                type="button"
                                text="Send Enquiry"
                                onClick={() => setIsOpenAskInquiry(true)}
                              />
                              {isAddedToCart ? (
                                <WebButton
                                  className="flex-1 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200"
                                  type="button"
                                  text="Go to Cart"
                                  onClick={() => navigate('/checkout/cart')}
                                />
                              ) : (
                                <WebButton
                                  className="flex-1 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-green-700 hover:to-green-800 transition-all duration-200"
                                  type="button"
                                  text="Add to Cart"
                                  onClick={handleAddToCart}
                                />
                              )}
                            </div>
                          ) : (
                            isAddedToCart ? (
                              <WebButton
                                className="w-full py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200"
                                type="button"
                                text="Go to Cart"
                                onClick={() => navigate('/checkout/cart')}
                              />
                            ) : (
                              <WebButton
                                className="w-full py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-green-700 hover:to-green-800 transition-all duration-200"
                                type="button"
                                text="Add to Cart"
                                onClick={handleAddToCart}
                              />
                            )
                          )}
                        </div>
                      </>
                    )}
                  </>
                )
              )}
            </div>

            {!askForPricingQuotation && !isWholesale && !loadingPricing && !autoUpdating && (
              <div className="py-3 border-t border-[#e5e7eb]">
                <div className="flex justify-between items-start">
                  <span className="text-[10px]" style={{ lineHeight: '1.2' }}>
                    <span className="text-[14px] font-semibold">Final Amount : </span>
                    <br /> (GST and Duties included)
                  </span>
                  <div className="text-right">
                    <div className="text-[18px] font-bold text-green-600">₹{hasPriceLogs && dbPriceTotal > 0 ? dbPriceTotal.toLocaleString() : total}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">₹{hasPriceLogs && dbAveragePrice > 0 ? dbAveragePrice.toFixed(2) : averagePrice.toFixed(2)}/piece</div>
                  </div>
                </div>

                {/* Add to Cart Button for Dropshipping */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                  {pricingData?.showWarning ? (
                    // Show both buttons in same row when warning is true
                    <div className="flex gap-3">
                      <WebButton
                        className="flex-1 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-orange-700 hover:to-orange-800 transition-all duration-200"
                        type="button"
                        text="Send Enquiry"
                        onClick={() => setIsOpenAskInquiry(true)}
                      />
                      {isAddedToCart ? (
                        <WebButton
                          className="flex-1 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200"
                          type="button"
                          text="Go to Cart"
                          onClick={() => navigate('/checkout/cart')}
                        />
                      ) : (
                        <WebButton
                          className="flex-1 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-green-700 hover:to-green-800 transition-all duration-200"
                          type="button"
                          text="Add to Cart"
                          onClick={handleAddToCart}
                        />
                      )}
                    </div>
                  ) : (
                    // Show single button when no warning
                    isAddedToCart ? (
                      <WebButton
                        className="w-full py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200"
                        type="button"
                        text="Go to Cart"
                        onClick={() => navigate('/checkout/cart')}
                      />
                    ) : (
                      <WebButton
                        className="w-full py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold text-[14px] rounded-lg shadow-sm hover:shadow-md hover:from-green-700 hover:to-green-800 transition-all duration-200"
                        type="button"
                        text="Add to Cart"
                        onClick={handleAddToCart}
                      />
                    )
                  )}
                </div>
              </div>
            )}

            {!askForPricingQuotation && isWholesale && !hasFetchedPricing && !autoUpdating && (
              <div className="pt-3">
                <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-[12px] text-gray-700 font-medium leading-tight">
                        Note: Prices are fetched in real-time from suppliers. The final price may vary based on current supplier pricing.
                      </p>
                    </div>
                  </div>
                </div>

                <WebButton
                  className="w-full py-2 bg-gradient-to-r from-gray-900 to-black text-white font-semibold text-[14px] rounded-full shadow-sm hover:shadow-md transition-all"
                  type="button"
                  onClick={() => handleWholesalePricingFetch(false)}
                  disabled={loadingPricing}
                  text={loadingPricing ? 'Updating...' : 'Get Final Price'}
                />
              </div>
            )}
          </>
        ) : (
          <div className="text-[12px] text-gray-500 text-center py-4">No variants selected</div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* DESKTOP: right sticky */}
      {!isMobile && (
        <div className="order-summary-container">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100 mx-[0px]">
            <h3 className="text-[16px] font-semibold mb-3 pb-2 border-b border-[#a9b4ca]">Price Details</h3>
            <PriceDetailsContent />
          </div>

          {hasZeroDimensions ? (
            <div className="flex gap-2 mb-0">
              <button
                type="button"
                onClick={() => setIsOpenAskInquiry(true)}
                className="flex-1 py-2 bg-white text-black hover:text-white border border-black rounded-full hover:bg-black transition-colors font-medium text-[14px]"
              >
                Send Inquiry
              </button>
            </div>
          ) : null}

          {/* Desktop-only Resell Message */}
          {!isMobile && pricingMode === 'dropshipping' && (
            <div className="mt-3 text-[12px] text-gray-700 px-1">
              <div className="font-semibold text-[16px]">Want to Sell This Product?</div>
              <div>Subscribe & Add it to your list, then sync to your Shopify or Amazon store.</div>
            </div>
          )}

          {/* Desktop — Subscribe button visible only in Dropshipping mode */}
          {!isMobile && pricingMode === 'dropshipping' && (
            <div className="mb-3 mt-2 px-1">
              <SubscribeButton product={product} searchKeyword={searchKeyword} isLoggedIn={isLoggedIn} sp={sp} priceRanges={priceRanges} variantDetails={variantDetails} />
            </div>
          )}

          <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center mb-3">
              <FaShieldAlt className="w-5 h-5 text-blue-600 mr-2" />
              <div className="flex items-end">
                <ComImportLogoImage className="h-4 w-auto align-middle" />
                <h4 className="text-[16px] font-semibold text-gray-800 ml-2">order protection</h4>
              </div>
            </div>

            <div className="space-y-3 text-[12px] text-gray-700">
              <div className="flex items-start">
                <div className="w-5 h-5 mr-2 mt-0.5">
                  <FaLock className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-[13px] flex items-center gap-2">
                    Secure payments*
                    <div className="flex items-center gap-1 ml-1">
                      <PaymentLogo src={visaLogo} alt="Visa" />
                      <PaymentLogo src={mastercardLogo} alt="Mastercard" />
                      <PaymentLogo src={upiLogo} alt="Upi" />
                      <PaymentLogo src={rupayLogo} alt="Rupay" />
                    </div>
                  </div>
                  <div className="text-gray-600 mt-0.5">Every payment you make on <ComImportLogoImage className="inline h-3 w-auto align-middle" /> is secured with strict SSL encryption</div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-5 h-5 mr-2 mt-0.5">
                  <FaTruck className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-[13px]">Delivery arranged by <ComImportLogoImage className="inline h-3 w-auto align-middle" />*</div>
                  <div className="text-gray-600 mt-0.5">Expect your order to be delivered before scheduled dates</div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-5 h-5 mr-2 mt-0.5">
                  <FaUndo className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-[13px]">Easy Return*</div>
                  <div className="text-gray-600 mt-0.5">Make free local returns for defects on qualifying request</div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-5 h-5 mr-2 mt-0.5">
                  <FaMoneyBillWave className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-[13px]">Full Money-back protection*</div>
                  <div className="text-gray-600 mt-0.5">Claim a refund if your order doesn't ship or is missing.</div>
                </div>
              </div>

              <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-100 text-[11px] text-black-800">
                Only orders placed and paid through <ComImportLogoImage className="inline h-3 w-auto align-middle" /> can enjoy free protection by <b>Trade Assurance</b>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE: bottom bar + dropdown sheet */}
      {isMobile && (
        <>
          {/* Blur overlay when tooltip is shown */}
          {showTooltip && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[1]" onClick={() => setShowTooltip(false)} />}

          <div className="os-mobile-container">
            {/* Top bar: three column layout - quantity left, center space, price right */}
            <div className="os-mobile-top-bar" aria-expanded={showTooltip}>
              <div className="os-mobile-left-col">
                <span className="os-qtn-large">Quantity: {totalQuantity || 0}</span>
              </div>

              <div className="os-mobile-center-col">
                {/* Center spacing column */}
              </div>

              <div className="os-mobile-right-col">
                {askForPricingQuotation ? (
                  <span className="text-red-600 text-[12px] font-medium">To get price info send inquiry</span>
                ) : (
                  <div className="flex items-center gap-1">
                    <div className="text-right">
                      <div className="os-top-label">{topBarLabel}</div>
                      <div className="os-top-amount">₹{Number(topBarAmount || 0).toLocaleString()}</div>
                    </div>
                    <div
                      className="flex items-center justify-center cursor-pointer ml-1"
                      onClick={e => {
                        e.stopPropagation();
                        setShowTooltip(prev => !prev);
                      }}
                      role="button"
                      aria-label="Show price details"
                    >
                      <IoInformationCircleOutline size={18} className="text-gray-600 hover:text-blue-600 transition-colors" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Full width button row */}
            {!hasZeroDimensions && (
              <div className="os-mobile-button-row">
                {isWholesale ? (
                  hasFetchedPricing && !loadingPricing && !autoUpdating ? (
                    // Show warning buttons if showWarning is true
                    pricingData?.showWarning ? (
                      <div className="os-mobile-button-grid">
                        <button
                          className="os-mobile-half-cta os-mobile-enquiry-cta"
                          onClick={e => {
                            e.stopPropagation();
                            setIsOpenAskInquiry(true);
                          }}
                        >
                          Send Enquiry
                        </button>
                        {isAddedToCart ? (
                          <button
                            className="os-mobile-half-cta !bg-blue-600"
                            onClick={e => {
                              e.stopPropagation();
                              navigate('/checkout/cart');
                            }}
                          >
                            Go to cart
                          </button>
                        ) : (
                          <button
                            className="os-mobile-half-cta"
                            onClick={e => {
                              e.stopPropagation();
                              handleAddToCart();
                            }}
                          >
                            Add to cart
                          </button>
                        )}
                      </div>
                    ) : (
                      // Normal single button
                      isAddedToCart ? (
                        <button
                          className="os-mobile-full-cta !bg-blue-600"
                          onClick={e => {
                            e.stopPropagation();
                            navigate('/checkout/cart');
                          }}
                        >
                          Go to cart
                        </button>
                      ) : (
                        <button
                          className="os-mobile-full-cta"
                          onClick={e => {
                            e.stopPropagation();
                            handleAddToCart();
                          }}
                          disabled={false}
                        >
                          Add to cart
                        </button>
                      )
                    )
                  ) : (
                    <button
                      className="os-mobile-full-cta"
                      onClick={handleMobileCTA}
                      disabled={loadingPricing || autoUpdating || !hasSelection}
                    >
                      {loadingPricing || autoUpdating ? (
                        'Updating...'
                      ) : (
                        'Get Final Price'
                      )}
                    </button>
                  )
                ) : (
                  isAddedToCart ? (
                    <button
                      className="os-mobile-full-cta !bg-blue-600"
                      onClick={e => {
                        e.stopPropagation();
                        navigate('/checkout/cart');
                      }}
                    >
                      Go to cart
                    </button>
                  ) : (
                    <button
                      className="os-mobile-full-cta"
                      onClick={e => {
                        e.stopPropagation();
                        handleAddToCart();
                      }}
                    >
                      Add to cart
                    </button>
                  )
                )}
              </div>
            )}

            {/* Send Inquiry button for zero dimensions */}
            {hasZeroDimensions && (
              <div className="os-mobile-button-row">
                <button type="button" onClick={() => setIsOpenAskInquiry(true)} className="os-mobile-full-cta">
                  Send Inquiry
                </button>
              </div>
            )}

            {/* Dropdown content (sheet) - shown on hover */}
            {showTooltip && (
              <div className="fixed inset-x-4 bottom-20 z-[9999] max-h-[70vh] overflow-y-auto" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
                <div className="os-mobile-sheet" onClick={e => e.stopPropagation()}>
                  <div className="os-mobile-sheet-inner">
                    <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
                      <h3 className="text-[16px] font-semibold mb-3 pb-2 border-b border-[#a9b4ca]">Price Details</h3>
                      <PriceDetailsContent />
                    </div>

                    {/* MOBILE: Resell text + Subscribe — only for dropshipping */}
                    {pricingMode === 'dropshipping' && (
                      <>
                        <div className="mt-3 text-[12px] text-gray-700">
                          <div className="font-semibold text-[16px]">Want to Resell This Product?</div>
                          <div>Subscribe & Add it to your list, then sync to your Shopify or Amazon store.</div>
                        </div>

                        <div className="mb-3 mt-2">
                          <SubscribeButton product={product} searchKeyword={searchKeyword} isLoggedIn={isLoggedIn} sp={sp} priceRanges={priceRanges} variantDetails={variantDetails} />
                        </div>
                      </>
                    )}

                    <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center mb-3">
                        <FaShieldAlt className="w-5 h-5 text-blue-600 mr-2" />
                        <div className="flex items-end">
                          <ComImportLogoImage className="h-4 w-auto align-middle" />
                          <h4 className="text-[16px] font-semibold text-gray-800 ml-2">order protection</h4>
                        </div>
                      </div>

                      <div className="space-y-3 text-[12px] text-gray-700">
                        <div className="flex items-start">
                          <div className="w-5 h-5 mr-2 mt-0.5">
                            <FaLock className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-[13px] flex items-center gap-2">
                              Secure payments*
                              <div className="flex items-center gap-1 ml-1">
                                <PaymentLogo src={visaLogo} alt="Visa" />
                                <PaymentLogo src={mastercardLogo} alt="Mastercard" />
                                <PaymentLogo src={upiLogo} alt="Upi" />
                                <PaymentLogo src={rupayLogo} alt="Rupay" />
                              </div>
                            </div>
                            <div className="text-gray-600 mt-0.5">Every payment you make on <ComImportLogoImage className="inline h-3 w-auto align-middle" /> is secured with strict SSL encryption</div>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-5 h-5 mr-2 mt-0.5">
                            <FaTruck className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-[13px]">Delivery arranged by <ComImportLogoImage className="inline h-3 w-auto align-middle" />*</div>
                            <div className="text-gray-600 mt-0.5">Expect your order to be delivered before scheduled dates</div>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-5 h-5 mr-2 mt-0.5">
                            <FaUndo className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-[13px]">Easy Return*</div>
                            <div className="text-gray-600 mt-0.5">Make free local returns for defects on qualifying request</div>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-5 h-5 mr-2 mt-0.5">
                            <FaMoneyBillWave className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-[13px]">Full Money-back protection*</div>
                            <div className="text-gray-600 mt-0.5">Claim a refund if your order doesn't ship or is missing.</div>
                          </div>
                        </div>

                        <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-100 text-[11px] text-blue-800">
                          Only orders placed and paid through <ComImportLogoImage className="inline h-3 w-auto align-middle" /> can enjoy free protection by <b>Trade Assurance</b>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="my-3 mt-0 mx-[0.25rem] border-[#9ea2aa]" />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default OrderSummary;
